import { CartQueryShippingAddress } from '@app/types/monolith/types';
import { trackGA4BeginCheckout } from '@app/utils/analytics';
import { Button, constants } from '@redbubble/design-system';
import { useIntl } from '@redbubble/next/intl';
import logger from '@redbubble/next/logger';
import type { UserInfo } from '@redbubble/next/types';
import { Item as AnalyticsItem } from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ShippingMethod } from '../../types/cart/ShippingMethod';
import {
  handleCreateOrder,
  handleOnApprove,
  handleOnCancel,
  handleOnShippingAddressChange,
  handleOnShippingOptionsChange,
} from './handlers';
import { AddressDetails as PaymentAddress } from './handlers/handleOnApprove';
import styles from './PayPalExpress.module.scss';
import { ErrorCodes, getPaymentErrorMessage } from './paymentErrorLookup';
import {
  type AuthorizationData,
  type AuthorizationResponse,
  type ButtonShapeOption,
  type PageType,
  type PayPalCheckout,
  type PayPalErrorCallback,
  type PayPalRequiredCart,
  type PaypalSdkAddress,
  type ShippingAddressChangeData,
  type ShippingOptionsChangeData,
} from './types';
import { useInitPayPal } from './useInitPayPal';

export interface PayPalExpressProps {
  cart: PayPalRequiredCart;
  loggableCart?: string;
  onShippingAddressChange?: (
    shippingAddress?: CartQueryShippingAddress
  ) => Promise<PayPalRequiredCart | undefined>;
  onShippingPreferenceChange?: (
    preference: ShippingMethod,
    shippingAddress?: CartQueryShippingAddress
  ) => Promise<PayPalRequiredCart | undefined>;
  onPayError: PayPalErrorCallback;
  onInitError?: PayPalErrorCallback;
  onCreateOrderError?: PayPalErrorCallback;
  onApproveOrderError?: PayPalErrorCallback;
  onCancel?: () => Promise<void>;
  selectorId: string;
  shippingAddressEditable?: boolean;
  shippingOptionsEditable?: boolean;
  analyticsItems?: (AnalyticsItem & { coupon: string })[];
  page: PageType;
  onStart?: () => void;
  getAddressOverride?: () => Promise<{
    paypalSdkShipping: PaypalSdkAddress;
    shipping: PaymentAddress;
    billing?: PaymentAddress;
  } | null>;
  email: string | null;
}

export const PayPalExpress: React.FC<PayPalExpressProps> = ({
  cart,
  loggableCart,
  onShippingAddressChange,
  onShippingPreferenceChange,
  onInitError,
  onPayError,
  onCreateOrderError,
  onApproveOrderError,
  onCancel,
  selectorId,
  shippingAddressEditable,
  shippingOptionsEditable,
  analyticsItems,
  page,
  onStart,
  getAddressOverride,
  email,
}) => {
  const [rendered, setRendered] = useState(false);
  const intl = useIntl();
  const [{ locale, currency }] = useUserInfo<UserInfo>();

  const cartRef = useRef(cart);
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  // Track if we've already reported an init error to avoid spamming
  const hasReportedInitError = useRef(false);

  const {
    doInit,
    doReset,
    initialising,
    clientDeviceData,
    paypalClient,
    paypalButton,
    error: initError,
  } = useInitPayPal({
    currency,
    locale,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  const onReset = useCallback(async () => {
    hasReportedInitError.current = false;
    await doReset();
    setRendered(false);
  }, [doReset, setRendered]);

  // PayPal is not available for zero-amount orders.
  const amount = cart?.summary.total;
  useEffect(() => {
    if (amount === 0) onReset();
  }, [amount, onReset]);

  // Initialise can happen multiple times in a single page view
  // as "doReset" resets everything that was previously initialised.
  useEffect(() => {
    if (!initialising && amount && !initError) {
      doInit().then((res) => {
        if (res && res.error && !hasReportedInitError.current) {
          hasReportedInitError.current = true;
          onInitError?.(
            getPaymentErrorMessage({
              code: ErrorCodes.INIT_FAILED,
            })
          );
        }
      });
    }
  }, [initialising, amount, initError, doInit, onInitError]);

  /**
   * Paypal handlers useRefs
   * The useRefs are used for the Paypal handlers, so that the handlers will always use the
   * latest state and props without the need for re-rendering the Paypal Button.
   *
   * When initializing the Paypal Button, Paypal SDK will only use the initial state was passed in during the
   * the initial render. If a state or prop changes, the Paypal Button will not re-render. Therefore,
   * stale states/props will be used after the initial render.
   *
   * Using the fresh states and props are important as the user can cancel out of the Paypal Modal, edit their
   * shipping and/or billing details, and then open up Paypal modal again.
   */
  const paypalOnCreateRef =
    useRef<(paypalClient: PayPalCheckout) => Promise<string>>();
  const paypalOnApproveRef =
    useRef<
      (
        paypalClient: PayPalCheckout,
        data: AuthorizationData
      ) => Promise<AuthorizationResponse>
    >();
  const paypalOnClickRef =
    useRef<(actions: { reject: () => Promise<void> }) => Promise<void>>();

  paypalOnCreateRef.current = useCallback(
    async (paypalClient: PayPalCheckout) => {
      const addressOverride = await getAddressOverride?.();

      return handleCreateOrder({
        cart: cartRef.current,
        loggableCart,
        currency,
        intl,
        paypalClient,
        onError: onCreateOrderError,
        shippingOverride: addressOverride?.paypalSdkShipping,
        shippingAddressEditable,
        shippingOptionsEditable,
        page,
      });
    },
    [
      currency,
      loggableCart,
      intl,
      getAddressOverride,
      shippingOptionsEditable,
      shippingAddressEditable,
      onCreateOrderError,
      page,
    ]
  );

  // NOTE(cy): The Paypal SDK doesn't specify the type of the `actions` prop.
  // I was able to derive it by logging the Paypal Button's onClick() signature to the console.
  paypalOnClickRef.current = useCallback(
    async (actions: { reject: () => Promise<void> }) => {
      // NOTE(cy): this prevent the paypal modal from opening if getAddressOverride() was passed as a prop but the addresses are not complete
      const addressOverride = await getAddressOverride?.();
      if (getAddressOverride && !addressOverride) {
        return actions.reject();
      }

      onStart?.();

      if (!analyticsItems) return;

      const cartTotal = cart?.summary.total;
      const couponCode = cart?.applied_coupon?.code;

      trackGA4BeginCheckout({
        currency,
        items: analyticsItems,
        value: cartTotal,
        couponCode,
      });
    },
    [analyticsItems, cart, currency, onStart, getAddressOverride]
  );

  paypalOnApproveRef.current = useCallback(
    async (paypalClient: PayPalCheckout, data: AuthorizationData) => {
      const addressOverride = await getAddressOverride?.();

      return handleOnApprove({
        paymentData: data,
        rbEmail: email,
        clientDeviceData,
        paypalClient,
        onError: onApproveOrderError,
        page,
        shippingOverride: addressOverride?.shipping,
        billingOverride: addressOverride?.billing,
      });
    },
    [clientDeviceData, email, getAddressOverride, onApproveOrderError, page]
  );

  const shippingAddressRef =
    useRef<ShippingAddressChangeData['shippingAddress']>();

  // Only try render the button once per "doInit" -> "doReset" cycle.
  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    if (!paypalButton || !paypalClient) return;
    if (initialising || rendered) return;
    if (amount === 0) return;
    if (
      !paypalOnClickRef.current ||
      !paypalOnApproveRef.current ||
      !paypalOnCreateRef.current
    )
      return;

    const allowShippingAddressChange =
      shippingAddressEditable && typeof onShippingAddressChange === 'function';
    const allowShippingMethodsChange =
      shippingOptionsEditable &&
      typeof onShippingPreferenceChange === 'function';
    setRendered(true);
    paypalButton({
      style: {
        tagline: false,
        height: 48,
        shape: 'pill' as ButtonShapeOption,
      },
      fundingSource: 'paypal',
      // NOTE (cy): important that `createOrder()` remains as a anonymous function, so that
      // the 'freshest' version of `paypalOnCreate.current` is invoked and the function remains
      // bound to the component's context (`this`).
      // The existence of the `paypalOnCreateRef.current` is already checked at the top of this useEffect()
      createOrder: () => paypalOnCreateRef.current!(paypalClient),
      // NOTE (cy): important that `onApprove()` remains as a anonymous function, so that
      // the 'freshest' version of `paypalOnApprove.current` is invoked and the function remains
      // bound to the component's context (`this`).
      // The existence of the `paypalOnApproveRef.current` is already checked at the top of this useEffect()
      onApprove: (data) => paypalOnApproveRef.current!(paypalClient, data),
      onError: (error) => {
        const message = 'An error occurred inside the PayPal button';
        logger.error(
          {
            err: { error },
            context: 'PayPalExpress',
            message,
          },
          message
        );

        // We map to a generic error message as we cannot parse any useful information
        // from "error" due to it just being a string.
        // Note that any useful error parsing is done by the "handleOnXXX" handlers
        // (e.g. handleOnApprove.ts).
        onPayError(
          getPaymentErrorMessage({
            code: ErrorCodes.CHARGE_FAILED_RETRY_ALLOWED,
          })
        );
      },
      // the existence of `paypalOnClickRef.current` is already checked at the top of this useEffect()
      // @ts-ignore: The Paypal types are not correct here. `actions` exposed as a prop for `onClick()
      onClick: (_, actions) => paypalOnClickRef.current!(actions),
      onShippingAddressChange: async (data: ShippingAddressChangeData) => {
        if (!allowShippingAddressChange) {
          return;
        }
        shippingAddressRef.current = data.shippingAddress;
        await handleOnShippingAddressChange({
          data,
          paypalClient,
          onShippingAddressChange,
          currency,
          intl,
          onZeroPayment: onReset,
          page,
        });
      },
      onShippingOptionsChange: async (data: ShippingOptionsChangeData) => {
        if (!allowShippingMethodsChange) {
          return;
        }

        await handleOnShippingOptionsChange({
          data,
          paypalClient,
          onShippingPreferenceChange,
          currency,
          intl,
          onZeroPayment: onReset,
          page,
          shippingAddress: shippingAddressRef.current,
        });
      },
      onCancel: async () => {
        if (!onCancel) {
          return;
        }

        shippingAddressRef.current = undefined;
        await handleOnCancel({ onCancel });
      },
    }).render(`#${selectorId}`);
  }, [
    amount,
    paypalButton,
    paypalClient,
    initialising,
    rendered,
    selectorId,
    currency,
    intl,
    clientDeviceData,
    email,
    onReset,
    onShippingPreferenceChange,
    onPayError,
    onApproveOrderError,
    onCreateOrderError,
    shippingAddressEditable,
    shippingOptionsEditable,
    cart,
    page,
  ]);

  // PayPal is not available for zero-amount orders.
  if (amount === 0) return null;

  if (initError) {
    return null;
  }

  // The PayPal button is rendered into this div.
  return (
    <div className={styles.container} id={selectorId} data-testid={selectorId}>
      {!rendered && (
        <Button
          type="button"
          intent={constants.PRIMARY}
          size={constants.LARGE}
          loading
          strong
          fluid
        >
          &nbsp;
        </Button>
      )}
    </div>
  );
};
